import React from 'react';
import { Modal, Button, Row, Col, Table } from 'react-bootstrap';
import { PaymentTransaction } from '../../../api/superAdminPayments/paymentsService';
import InvoiceLogo from '../../../assets/images/admin-logos/Invoice-logo.png';
import SharedDownloadOverlay from '../../../components/SharedDownloadOverlay';
import { withLayoutProtection } from '../../../utils/layoutProtection';
import { generateHTMLToPDF } from '../../../utils/htmlToPdfService';

interface InvoiceModalProps {
  show: boolean;
  onHide: () => void;
  transaction: PaymentTransaction | null;
  generatePDF?: boolean; // New prop for PDF generation mode
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({ show, onHide, transaction, generatePDF = false }) => {
  const [isDownloading, setIsDownloading] = React.useState(false);

  if (!transaction) return null;

  // Generate invoice data locally (replacing the old service function)
  const generateInvoiceData = (transaction: PaymentTransaction) => {
    const now = new Date();
    const invoiceNumber = `INV-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    
    // Calculate base amount excluding 18% IGST
    const total = transaction.amount || 0;
    const baseAmount = Math.round((total / 1.18) * 100) / 100;
    const igst = Math.round((baseAmount * 0.18) * 100) / 100;

    const tournamentName = transaction.tournamentInfo?.name || 'Tournament';
    const teamName = transaction.teamInfo?.name || 'Team';
    const ageCategory = transaction.teamInfo?.ageCategory; // Get age category from teamInfo
    const playersCount = transaction.playersCount || 1;
    const transactionId = transaction.paymentId || transaction._id || 'N/A';

    // Get club info from teamInfo.clubInfo (updated structure)
    const clubInfo = transaction.teamInfo?.clubInfo;
    const clubName = clubInfo?.name || teamName; // Use club name, fallback to team name
    const clubAddress = clubInfo?.address || null;
    const clubGst = clubInfo?.gstNum || null; // API uses gstNum

    return {
      invoiceNumber,
      billedFrom: {
        companyName: "Jambavan Academy Private Limited",
        address: "Villa 183, Adarsh Palm Retreat, Lane 8, Bellandur, Bengaluru, Karnataka 560103",
        email: "contact@sportsforlife.in",
        gstNumber: "29ABCDE1234F1Z5"
      },
      billedTo: {
        customerName: clubName,
        clubName: clubName,
        teamName: teamName, // Add team name
        address: clubAddress,
        email: null, // Add missing email field
        gstNumber: clubGst
      },
      invoiceDate: now.toLocaleDateString('en-GB'),
      issuedOn: now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      items: [{
        description: `${tournamentName} - ${teamName} - ${ageCategory || 'N/A'}`,
        tournamentName: tournamentName,
        teamName: teamName,
        ageCategory: ageCategory || 'N/A',
        playersCount,
        transactionId,
        totalAmount: Math.round(baseAmount * 100) / 100
      }],
      summary: {
        subtotal: Math.round(baseAmount * 100) / 100,
        igst: Math.round(igst * 100) / 100,
        total: Math.round(total * 100) / 100
      },
      paymentInstructions: [
        "(i) 18% IGST will be applicable on all invoices",
        "(ii) For Delhi Based clients, GST must be split equally between Center and State and reflected on the invoice"
      ],
      gstId: "29ABCDE1234F1Z5",
      additionalNotes: "Fixture will be shared once approved"
    };
  };

  const invoiceData = generateInvoiceData(transaction);

  // Helper function for currency formatting
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Convert logo to base64 for PDF generation
  const getLogoBase64 = async (): Promise<string> => {
    try {
      const response = await fetch(InvoiceLogo);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting logo to base64:', error);
      // Fallback to text-based logo if image fails
      return '';
    }
  };

  // PDF generation function using direct canvas drawing (unified approach)
  const generatePDFFromModal = async () => {
    try {
      // Get logo as base64
      const logoBase64 = await getLogoBase64();
      
      // Create a completely off-screen canvas for PDF generation
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // Set canvas dimensions for A4 PDF
      const pdfWidth = 800;
      const pdfHeight = 1200; // Approximate A4 height
      canvas.width = pdfWidth;
      canvas.height = pdfHeight;

      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pdfWidth, pdfHeight);

      // Set font styles
      ctx.font = '16px Arial, sans-serif';
      ctx.fillStyle = '#333';

      let currentY = 40; // Starting Y position

      // Draw logo
      if (logoBase64) {
        const logoImg = new Image();
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          logoImg.src = logoBase64;
        });
        
        // Draw logo on the left side
        ctx.drawImage(logoImg, 40, currentY, 120, 80);
        currentY += 120; // Move down after logo
      } else {
        // Fallback logo if image fails
        ctx.fillStyle = '#0077F9';
        ctx.fillRect(40, currentY, 120, 80);
        ctx.fillStyle = '#ffffff';
        ctx.font = '32px Arial, sans-serif';
        ctx.fillText('SFL', 85, currentY + 50);
        currentY += 120;
      }

      // Draw header line
      ctx.strokeStyle = '#f8f9fa';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(40, currentY);
      ctx.lineTo(pdfWidth - 40, currentY);
      ctx.stroke();
      currentY += 30;

      // Draw billing section
      ctx.font = '16px Arial, sans-serif';
      ctx.fillStyle = '#495057';
      ctx.fillText('Billed From:', 40, currentY);
      currentY += 25;

      ctx.font = '16px Arial, sans-serif';
      ctx.fillStyle = '#0077F9';
      ctx.fillText(invoiceData.billedFrom.companyName, 40, currentY);
      currentY += 20;

      ctx.font = '14px Arial, sans-serif';
      ctx.fillStyle = '#333';
      ctx.fillText(invoiceData.billedFrom.address, 40, currentY);
      currentY += 20;

      ctx.fillStyle = '#0077F9';
      ctx.fillText(invoiceData.billedFrom.email, 40, currentY);
      currentY += 20;

      ctx.font = '13px Arial, sans-serif';
      ctx.fillStyle = '#666';
      ctx.fillText(`GST ID: ${invoiceData.billedFrom.gstNumber}`, 40, currentY);
      currentY += 30;

      // Draw "Billed To" section on the right - FIXED: Use fixed Y position to avoid clubbing
      const rightX = pdfWidth / 2 + 20;
      const billedToStartY = 160; // Fixed starting position for Billed To
      ctx.font = '16px Arial, sans-serif';
      ctx.fillStyle = '#495057';
      ctx.fillText('Billed To:', rightX, billedToStartY);

      ctx.font = '16px Arial, sans-serif';
      ctx.fillStyle = '#333';
      ctx.fillText(invoiceData.billedTo.clubName, rightX, billedToStartY + 25);

      // Add team name
      if (invoiceData.billedTo.teamName) {
        ctx.font = '13px Arial, sans-serif';
        ctx.fillStyle = '#666';
        ctx.fillText(`Team: ${invoiceData.billedTo.teamName}`, rightX, billedToStartY + 45);
      }

      if (invoiceData.billedTo.address) {
        ctx.font = '14px Arial, sans-serif';
        ctx.fillStyle = '#333';
        ctx.fillText(invoiceData.billedTo.address, rightX, billedToStartY + 65);
      }

      if (invoiceData.billedTo.gstNumber) {
        ctx.font = '13px Arial, sans-serif';
        ctx.fillStyle = '#666';
        ctx.fillText(`GST ID: ${invoiceData.billedTo.gstNumber}`, rightX, billedToStartY + 85);
      }

      // Reset Y position for next section
      currentY = Math.max(currentY, 200);

      // Draw "Issued on" date
      ctx.font = '14px Arial, sans-serif';
      ctx.fillStyle = '#666';
      ctx.textAlign = 'right';
      ctx.fillText(`Issued on: ${invoiceData.issuedOn}`, pdfWidth - 40, currentY);
      ctx.textAlign = 'left';
      currentY += 40;

      // Draw invoice table - FIXED: Better column spacing to avoid clubbing
      ctx.font = '14px Arial, sans-serif';
      ctx.fillStyle = '#495057';
      ctx.fillText('ITEM', 40, currentY);
      ctx.fillText('TRANSACTION ID', 400, currentY); // Moved further right
      ctx.fillText('TOTAL AMOUNT', 650, currentY); // Moved further right
      currentY += 25;

      // Draw table line
      ctx.strokeStyle = '#dee2e6';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(40, currentY);
      ctx.lineTo(pdfWidth - 40, currentY);
      ctx.stroke();
      currentY += 20;

      // Draw table data - FIXED: Match new column positions
      ctx.font = '14px Arial, sans-serif';
      ctx.fillStyle = '#333';
      
      invoiceData.items.forEach(item => {
        ctx.fillText(item.description, 40, currentY);
        ctx.fillText(item.transactionId, 400, currentY); // Match new position
        ctx.textAlign = 'right';
        ctx.fillText(formatCurrency(item.totalAmount), 650, currentY); // Match new position
        ctx.textAlign = 'left';
        currentY += 20;
      });

      currentY += 20;

      // Draw summary table - FIXED: Wider table to prevent truncation
      const summaryX = pdfWidth - 400; // Increased width
      ctx.font = '14px Arial, sans-serif';
      ctx.fillStyle = '#495057';
      ctx.fillText('ITEM', summaryX, currentY);
      ctx.fillText('AMOUNT', summaryX + 300, currentY); // Increased spacing
      currentY += 25;

      // Draw summary line
      ctx.strokeStyle = '#dee2e6';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(summaryX, currentY);
      ctx.lineTo(summaryX + 350, currentY); // Increased line width
      ctx.stroke();
      currentY += 20;

      // Draw summary data
      ctx.font = '14px Arial, sans-serif';
      ctx.fillStyle = '#333';
      
      // Team Name - FIXED: Use team name from billedTo instead of description
      ctx.fillText('Team Name:', summaryX, currentY);
      ctx.textAlign = 'right';
      ctx.fillText(invoiceData.billedTo.teamName || 'Team', summaryX + 300, currentY);
      ctx.textAlign = 'left';
      currentY += 20;

      // Subtotal
      ctx.fillText('Subtotal:', summaryX, currentY);
      ctx.textAlign = 'right';
      ctx.fillText(formatCurrency(invoiceData.summary.subtotal), summaryX + 300, currentY);
      ctx.textAlign = 'left';
      currentY += 20;

      // IGST
      ctx.fillText('IGST(18%):', summaryX, currentY);
      ctx.textAlign = 'right';
      ctx.fillText(formatCurrency(invoiceData.summary.igst), summaryX + 300, currentY);
      ctx.textAlign = 'left';
      currentY += 20;

      // Total
      ctx.font = '16px Arial, sans-serif';
      ctx.fillStyle = '#0077F9';
      ctx.fillText('Total:', summaryX, currentY);
      ctx.textAlign = 'right';
      ctx.fillText(formatCurrency(invoiceData.summary.total), summaryX + 300, currentY);
      ctx.textAlign = 'left';

      // Convert canvas to PDF using jsPDF
      const { default: jsPDF } = await import('jspdf');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgData = canvas.toDataURL('image/png');
      const finalPdfWidth = pdf.internal.pageSize.getWidth();
      const finalPdfHeight = (canvas.height * finalPdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, finalPdfWidth, finalPdfHeight);

      // Generate filename
      const tournamentName = transaction.tournamentInfo?.name?.replace(/\s+/g, '-') || 'Tournament';
      const teamName = transaction.teamInfo?.name?.replace(/\s+/g, '-') || 'Team';
      const filename = `invoice-${tournamentName}-${teamName}.pdf`;

      // Download PDF
      pdf.save(filename);

      // Close modal if in PDF generation mode
      if (generatePDF) {
        onHide();
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error('Failed to generate PDF');
    }
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      
      // Use layout protection wrapper for consistent behavior
      await withLayoutProtection(async () => {
        // Generate unified invoice data
        const unifiedInvoiceData = generateInvoiceData(transaction);
        
        // Use HTML-to-PDF for exact UI replica
        const pdfBlob = await generateHTMLToPDF(unifiedInvoiceData);
        
        // Create download link
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        
        // Generate filename
        const tournamentName = transaction.tournamentInfo?.name?.replace(/\s+/g, '-') || 'Tournament';
        const teamName = transaction.teamInfo?.name?.replace(/\s+/g, '-') || 'Team';
        link.download = `invoice-${tournamentName}-${teamName}.pdf`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Close modal if in PDF generation mode
        if (generatePDF) {
          onHide();
        }
      });
      
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg"
      centered
      className="invoice-modal"
    >
      <Modal.Header closeButton style={{ borderBottom: '2px solid #f8f9fa', padding: '20px 24px' }}>
        <Modal.Title className="d-flex align-items-center w-100">
          <div>
            <img 
              src={InvoiceLogo} 
              alt="SFL Play To Progress" 
              style={{ 
                height: '60px', 
                width: 'auto',
                maxWidth: '200px'
              }}
            />
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '24px' }}>
        {/* Company and Customer Details */}
        <Row className="mb-4">
          <Col md={6}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '16px', color: '#495057' }}>
                Billed From:
              </div>
              <div style={{ fontSize: '14px', lineHeight: '1.4', color: '#333' }}>
                <div style={{ fontWeight: '600', fontSize: '16px', color: '#0077F9', marginBottom: '5px' }}>
                  {invoiceData.billedFrom.companyName}
                </div>
                <div style={{ marginBottom: '3px' }}>{invoiceData.billedFrom.address}</div>
                <div style={{ color: '#0077F9', textDecoration: 'underline' }}>{invoiceData.billedFrom.email}</div>
                <div style={{ marginTop: '5px', fontSize: '13px', color: '#666' }}>
                  GST ID: {invoiceData.billedFrom.gstNumber}
                </div>
              </div>
            </div>
          </Col>
          <Col md={6}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '16px', color: '#495057' }}>
                Billed To:
              </div>
              <div style={{ fontSize: '14px', lineHeight: '1.4', color: '#333' }}>
                <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '5px' }}>
                  {invoiceData.billedTo.clubName}
                </div>
                {invoiceData.billedTo.teamName && (
                  <div style={{ marginBottom: '3px', fontSize: '13px', color: '#666' }}>
                    Team: {invoiceData.billedTo.teamName}
                  </div>
                )}
                {invoiceData.billedTo.address && (
                  <div style={{ marginBottom: '3px' }}>{invoiceData.billedTo.address}</div>
                )}
                {invoiceData.billedTo.gstNumber && (
                  <div style={{ marginTop: '5px', fontSize: '13px', color: '#666' }}>
                    GST ID: {invoiceData.billedTo.gstNumber}
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Invoice Date */}
        <Row className="mb-4">
          <Col md={6}>
            <div style={{ marginBottom: '15px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#495057', marginBottom: '5px' }}>
                Issued on:
              </div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#000' }}>
                {invoiceData.issuedOn}
            </div>
            </div>
          </Col>
        </Row>

        {/* Payment Details Table */}
        <div className="mb-4">
          <Table bordered style={{ fontSize: '14px' }}>
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', textTransform: 'uppercase', color: '#495057' }}>ITEM</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', textTransform: 'uppercase', color: '#495057' }}>TRANSACTION ID</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', textTransform: 'uppercase', color: '#495057' }}>TOTAL AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.items.map((item, index) => (
                <tr key={index}>
                  <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                    <div style={{ lineHeight: '1.4' }}>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                        Tournament name: {item.tournamentName}
                      </div>
                      <div style={{ marginBottom: '4px' }}>
                        Team name: {item.teamName}
                      </div>
                      <div>
                        Age category: {item.ageCategory}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6' }}>{item.transactionId}</td>
                  <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency(item.totalAmount)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Summary Table */}
        <Row className="mb-4">
          <Col md={{ span: 6, offset: 6 }}>
            <Table bordered style={{ fontSize: '14px' }}>
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ padding: '8px', fontWeight: 'bold', textTransform: 'uppercase', color: '#495057', border: '1px solid #dee2e6' }}>ITEM</th>
                  <th style={{ padding: '8px', fontWeight: 'bold', textTransform: 'uppercase', color: '#495057', border: '1px solid #dee2e6', textAlign: 'right' }}>AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #dee2e6', fontWeight: '500' }}>
                    <div style={{ lineHeight: '1.4' }}>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                        Tournament name: {invoiceData.items[0]?.tournamentName || 'N/A'}
                      </div>
                      <div style={{ marginBottom: '4px' }}>
                        Team name: {invoiceData.items[0]?.teamName || 'N/A'}
                      </div>
                      <div>
                        Age category: {invoiceData.items[0]?.ageCategory || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'right' }}>
                    {/* Empty cell for alignment */}
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: '8px', border: '1px solid #dee2e6', fontWeight: '500' }}>Subtotal:</td>
                  <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'right' }}>
                    {formatCurrency(invoiceData.summary.subtotal)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #dee2e6', fontWeight: '500' }}>IGST(18%):</td>
                  <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'right' }}>
                    {formatCurrency(invoiceData.summary.igst)}
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#f8f9fa', color: '#000' }}>
                  <td style={{ padding: '12px', fontWeight: 'bold' }}>Total:</td>
                  <td style={{ padding: '12px', fontWeight: 'bold', textAlign: 'right', color: '#0077F9' }}>
                    {formatCurrency(invoiceData.summary.total)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Col>
        </Row>


      </Modal.Body>

      <Modal.Footer style={{ borderTop: '2px solid #f8f9fa', padding: '20px 24px' }}>
        <div className="d-flex justify-content-end gap-3">
          <Button 
            variant="primary" 
            onClick={handleDownload}
            disabled={isDownloading}
            className="me-2"
            style={{ minWidth: '100px' }}
          >
            {isDownloading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Generating...
              </>
            ) : (
              <>
                <i className="ph-download me-2"></i>
                Download PDF
              </>
            )}
          </Button>
          <Button 
            variant="outline-secondary" 
            onClick={onHide}
            disabled={isDownloading}
            style={{ minWidth: '100px' }}
          >
            <i className="ph-x me-2"></i>
            Close
          </Button>
        </div>
      </Modal.Footer>
      
      {/* Shared Download Overlay for consistent experience */}
      <SharedDownloadOverlay 
        isVisible={isDownloading} 
        message="Generating Invoice..." 
      />
    </Modal>
  );
};

export default InvoiceModal; 